/**
 * The first run, as one decision (owner brief, 2026-08-21).
 *
 * The owner's sequence is: log in, slides, portal door, mandatory permission,
 * optional permissions, guided tour. Before this function existed, three
 * different components each held a piece of that order and none of them held
 * the order itself, which is how a first run ended up showing no permission
 * dialog at all and never starting the tour.
 *
 * This is deliberately a pure function over plain state, not a hook: the order
 * of a first run is the kind of thing that should be readable in one screen
 * and testable without a browser. `ConsentGate` and `TourAutoStart` render the
 * stage it returns; neither decides anything on its own.
 */

/** Everything the sequence needs to know. All of it already exists upstream. */
export interface FirstRunInput {
    /** Consent + profile have been read. Nothing decides before this. */
    loaded: boolean;
    /** The entry (portal door) animation is over, or was never owed. */
    entrySettled: boolean;
    /** Profile flag. Null while unknown, which is not the same as false. */
    hasOnboarded: boolean | null;
    /** Profile flag. Null while unknown. */
    hasToured: boolean | null;
    /** No Bucket C row yet: the mandatory question is outstanding. */
    needsFirstRunConsent: boolean;
    /** Bucket A or B has never been answered. */
    needsBucketABDecision: boolean;
    /** The tour walks the sidebar and the Lab, neither of which exists on a
     *  phone-sized viewport. */
    viewportTooSmallForTour: boolean;
}

export type FirstRunStage =
    /** Wait. Either we do not know enough yet, or something is still playing. */
    | 'blocked'
    /** Ask the mandatory data-processing consent (Bucket C). */
    | 'consent-required'
    /** Ask the two optional consents (Buckets A and B), together. */
    | 'consent-optional'
    /** Start the guided tour. */
    | 'tour'
    /** Nothing outstanding. The overwhelming majority of sessions land here. */
    | 'settled';

export function firstRunStage(input: FirstRunInput): FirstRunStage {
    const {
        loaded,
        entrySettled,
        hasOnboarded,
        hasToured,
        needsFirstRunConsent,
        needsBucketABDecision,
        viewportTooSmallForTour,
    } = input;

    // `hasOnboarded !== true` rather than `=== false`: null means the profile
    // read has not resolved (or failed), and a first run must not be guessed
    // at. Guessing is what put a consent dialog on top of the slide sequence
    // in an earlier design.
    if (!loaded || !entrySettled || hasOnboarded !== true) return 'blocked';

    // Consent before orientation, always. The mandatory bucket is what makes
    // storing the user's data lawful at all, so it precedes the optional pair,
    // and both precede the tour.
    if (needsFirstRunConsent) return 'consent-required';
    if (needsBucketABDecision) return 'consent-optional';

    // A tour is owed only to someone who has never had one. `null` is unknown,
    // and an unknown never triggers a walkthrough.
    if (hasToured === false && !viewportTooSmallForTour) return 'tour';

    return 'settled';
}
